import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as FormIcon,
  CheckCircle as CheckIcon,
  Public,
} from '@mui/icons-material';
import { formSuggestionService, FormSuggestion } from '../services/formSuggestionService';
import { formService } from '../services/formService';

interface FormSuggestionProps {
  messages: Array<{content: string, type: string}>;
  jurisdiction?: string;
  onFormDownload?: (formId: string) => void;
  onSuggestionsLoaded?: (hasSuggestions: boolean) => void;
  preloadedSuggestions?: FormSuggestion[]; // Pre-fetched suggestions stored with the message
  preloadedJurisdiction?: string; // Jurisdiction used when pre-fetching
}

const FormSuggestionComponent: React.FC<FormSuggestionProps> = ({
  messages,
  jurisdiction,
  onFormDownload,
  onSuggestionsLoaded,
  preloadedSuggestions,
  preloadedJurisdiction,
}) => {
  const [suggestions, setSuggestions] = useState<FormSuggestion[]>(preloadedSuggestions || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const formSuggestions = await formSuggestionService.getTopFormSuggestions(
        messages,
        jurisdiction,
        3
      );
      setSuggestions(formSuggestions);
      onSuggestionsLoaded?.(formSuggestions.length > 0);
    } catch (err) {
      setError('Failed to load form suggestions');
      console.error('Error loading form suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [messages, jurisdiction, onSuggestionsLoaded]);

  // Only fetch suggestions if:
  // 1. We don't have preloaded suggestions, OR
  // 2. The jurisdiction has changed from when suggestions were preloaded
  useEffect(() => {
    if (preloadedSuggestions && preloadedSuggestions.length > 0) {
      // Use preloaded suggestions if available and jurisdiction hasn't changed
      if (preloadedJurisdiction === jurisdiction || (!preloadedJurisdiction && !jurisdiction)) {
        setSuggestions(preloadedSuggestions);
        onSuggestionsLoaded?.(preloadedSuggestions.length > 0);
        return;
      }
    }
    
    // Only fetch if we don't have preloaded suggestions or jurisdiction changed
    if (messages.length > 0) {
      loadSuggestions();
    }
  }, [messages, jurisdiction, preloadedSuggestions, preloadedJurisdiction, loadSuggestions, onSuggestionsLoaded]);

  const handleDownload = async (formId: string) => {
    setDownloading(formId);
    try {
      await formService.downloadFormTemplate(formId);
      onFormDownload?.(formId);
    } catch (err) {
      console.error('Error downloading form:', err);
    } finally {
      setDownloading(null);
    }
  };

  const getJurisdictionLabel = (jurisdiction: string) => {
    const labels: { [key: string]: string } = {
      'us_fda': 'US FDA',
      'eu_mdr': 'EU MDR',
    };
    return labels[jurisdiction] || jurisdiction;
  };

  const getRelevanceColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'default';
  };

  const getRelevanceLabel = (score: number) => {
    if (score >= 80) return 'Highly Relevant';
    if (score >= 60) return 'Very Relevant';
    if (score >= 40) return 'Relevant';
    return 'Somewhat Relevant';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Analyzing conversation for relevant forms...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Render the suggestions content
  const renderSuggestionsContent = () => {
    if (suggestions.length === 0) {
      // If no jurisdiction was detected, show a helpful message
      if (!jurisdiction) {
        return (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Public sx={{ fontSize: 18, color: '#1976d2' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                Jurisdiction Required
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              To provide relevant regulatory forms, please select a regulatory authority from the dropdown above or mention the jurisdiction in your question.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              You can mention the jurisdiction in your question (e.g., "FDA requirements for..." or "EU MDR compliance for...")
            </Typography>
          </Box>
        );
      }
      // If jurisdiction exists but no suggestions found, show a message
      return (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FormIcon sx={{ fontSize: 18, color: '#666' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
              No Forms Found
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            No relevant forms were found for {getJurisdictionLabel(jurisdiction)} based on your conversation. 
            Try asking more specific questions about forms or compliance requirements.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {suggestions.map((suggestion, index) => (
          <React.Fragment key={suggestion.form.id}>
            <Box
              sx={{
                p: 2,
                border: '1px solid #f0f0f0',
                borderRadius: 1,
                backgroundColor: '#fafafa',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {suggestion.form.form_code} - {suggestion.form.form_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {suggestion.form.form_description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <Chip
                    label={getRelevanceLabel(suggestion.relevanceScore)}
                    size="small"
                    color={getRelevanceColor(suggestion.relevanceScore) as any}
                    icon={<CheckIcon />}
                  />
                  <Chip
                    label={`${suggestion.relevanceScore}%`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={getJurisdictionLabel(suggestion.form.jurisdiction)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={suggestion.form.form_type}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={suggestion.form.form_category}
                  size="small"
                  variant="outlined"
                />
              </Box>

              {suggestion.keywords.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Matched keywords:
                  </Typography>
                  {suggestion.keywords.slice(0, 5).map((keyword, idx) => (
                    <Chip
                      key={idx}
                      label={keyword}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                  {suggestion.keywords.length > 5 && (
                    <Typography variant="caption" color="text.secondary">
                      +{suggestion.keywords.length - 5} more
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', flex: 1, minWidth: 200 }}>
                  {suggestion.reason}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Show direct download link if available */}
                  {(suggestion.form.s3_url || suggestion.form.original_url || suggestion.form.source_page_url) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const url = suggestion.form.s3_url || suggestion.form.original_url || suggestion.form.source_page_url;
                        if (url) {
                          window.open(url, '_blank');
                        }
                      }}
                      sx={{ minWidth: 100 }}
                    >
                      View Link
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={downloading === suggestion.form.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => handleDownload(suggestion.form.id)}
                    disabled={downloading === suggestion.form.id}
                    sx={{ minWidth: 120 }}
                  >
                    {downloading === suggestion.form.id ? 'Downloading...' : 'Download'}
                  </Button>
                </Box>
              </Box>
            </Box>
            {index < suggestions.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  // For tab usage, return content directly without card wrapper
  if (onSuggestionsLoaded) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Other relevant compliance forms that may be useful for your query:
        </Typography>
        {renderSuggestionsContent()}
      </Box>
    );
  }

  // For standalone usage (legacy), return with card wrapper
  return (
    <Card sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Other Reference Forms
            </Typography>
            <Chip
              label={`${suggestions.length} forms`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Other relevant compliance forms that may be useful for your query:
        </Typography>

        <Collapse in={expanded}>
          {renderSuggestionsContent()}
        </Collapse>

        {!expanded && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Click to view {suggestions.length} other reference forms
            </Typography>
            <Button
              size="small"
              onClick={() => setExpanded(true)}
              endIcon={<ExpandMoreIcon />}
            >
              View Forms
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FormSuggestionComponent;
